import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormHelperText,
  Stack,
  SelectChangeEvent,
  Grid,
  IconButton,
  InputLabel,
} from "@mui/material";
import { ErrorDisplay } from "./ErrorDisplay";
import { ApiError } from "@/types/api";
import { LoadingButton } from "@mui/lab";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import styled from "styled-components";
import { Card, CardContent } from "./card";
import { Input } from "@components/ui/input";
import { maskCep, maskCPF, maskCpfCnpj, maskPhone } from "@/utils/maskUtils";
import { Camera, Check, FileArchive } from "lucide-react";
import { Textarea } from "./textarea";

const LogoPreviewContainer = styled.div`
  display: flex;
  align-items: flex-start;
  margin-top: 4px;
  gap: 16px;
`;

const LogoPreviewWrapper = styled.div`
  width: 100px;
  height: 100px;
  background-image: linear-gradient(45deg, #f0f0f0 25%, transparent 25%),
    linear-gradient(-45deg, #f0f0f0 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #f0f0f0 75%),
    linear-gradient(-45deg, transparent 75%, #f0f0f0 75%);
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
  border: 1px solid #ddd;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LogoPreview = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 4px;
`;

const HiddenInput = styled.input`
  display: none;
`;

const UploadButton = styled(Button)`
  margin-top: 8px;
`;

export interface Option {
  value: string | number;
  label: string;
}

export interface SelectOption {
  key: string;
  value: string;
  hidden?: boolean;
}

export interface LogoFieldProps {
  name: string;
  label: string;
  accept?: string;
  helperText?: string;
  error?: boolean;
  validationError?: string;
  previewUrl?: string | null;
  value?: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
}

export const LogoField: React.FC<LogoFieldProps> = ({
  name,
  label,
  accept,
  helperText,
  error,
  validationError,
  previewUrl,
  value,
  onChange,
  required,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleButtonClick = () => {
    inputRef.current?.click();
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event);
  };

  const FloatingLabel = styled(Typography)<{ isActive: boolean }>`
    transform: translate(0px, -20px);
    color: #666;
    transition: all 0.3s ease-in-out;
  `;

  return (
    <FormControl fullWidth error={error} variant="outlined">
      <FloatingLabel isActive={true}>
        <Box sx={{ transform: "translate(10px, 10px)" }}>
          <Typography
            sx={{
              backgroundColor: "white",
              padding: "0 4px",
              color: error ? "error.main" : "#666",
            }}
            variant="caption"
          >
            {label}
          </Typography>
        </Box>
        <Box
          sx={{
            border: "1px solid",
            borderColor: error ? "error.main" : "rgba(0, 0, 0, 0.23)",
            borderRadius: 1,
            p: 2,
            "&:hover": {
              borderColor: error ? "error.main" : "text.primary",
            },
          }}
        >
          <LogoPreviewContainer>
            {(previewUrl || value) && (
              <LogoPreviewWrapper>
                <LogoPreview
                  src={previewUrl || value || ""}
                  alt="Logo preview"
                  onError={(e) => {
                    console.error("Error loading image:", e.currentTarget.src);
                    const img = e.target as HTMLImageElement;
                    img.style.display = "none";
                  }}
                />
              </LogoPreviewWrapper>
            )}
            <Box
              sx={{
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                minHeight: "100px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box sx={{ flexShrink: 0, minWidth: "140px" }}>
                  <HiddenInput
                    ref={inputRef}
                    type="file"
                    name={name}
                    onChange={handleChange}
                    required={required}
                    accept={accept}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleButtonClick}
                    fullWidth
                    startIcon={<AddIcon />}
                    size="medium"
                  >
                    Escolher arquivo
                  </Button>
                </Box>
              </Box>
              {(helperText || validationError) && (
                <FormHelperText error={error} sx={{ mt: 1, ml: 0 }}>
                  {validationError || helperText}
                </FormHelperText>
              )}
            </Box>
          </LogoPreviewContainer>
        </Box>
      </FloatingLabel>
    </FormControl>
  );
};

export interface Field {
  name: string;
  label: string;
  type?:
    | "section"
    | "separator"
    | "toogle"
    | "image"
    | "document"
    | "text"
    | "textarea"
    | "number"
    | "email"
    | "password"
    | "date"
    | "datetime-local"
    | "select"
    | "multiselect"
    | "checkbox"
    | "radio"
    | "select-builder"
    | "file"
    | "logo"
    | "custom";
  required?: boolean;
  options?: Option[];
  multiple?: boolean;
  min?: number | string;
  max?: number | string;
  pattern?: string;
  placeholder?: string;
  disabled?: boolean;
  rows?: number;
  validationError?: string;
  onChange?: (value: any) => void;
  value?: any;
  showOptions?: boolean;
  selectOptions?: SelectOption[];
  onSelectOptionsChange?: (options: SelectOption[]) => void;
  accept?: string;
  helperText?: string;
  error?: boolean;
  previewUrl?: string | null;
}

interface DataFormProps<T> {
  data: Partial<T>;
  setFormDataParent: any;
  fields: Field[];
  onSubmit: (data: Partial<T>) => Promise<void>;
  basePath: string;
  title: string;
  isEdit?: boolean;
  hideButtons?: boolean;
  error?: ApiError | null;
  onClearError?: () => void;
  onCancel?: () => void;
  showValidationErrorsInFields?: boolean;
  disabled?: boolean;
  onChange?: (name: string, value: any) => void;
  children?: any;
  onClose?: any;
}

const DataForm = <T extends object>({
  data,
  setFormDataParent,
  fields,
  onSubmit,
  basePath,
  title,
  isEdit,
  isForm,
  hideButtons,
  error,
  onClearError,
  onCancel,
  disabled = false,
  onChange,
  children,
  onClose,
}: DataFormProps<T>) => {
  const id_submit = Math.random();
  if (!setFormDataParent) {
    setFormDataParent = (a: any) => {};
  }
  // console.log('🎯 DataForm - Props:', {
  //   data,
  //   fields: fields.map(f => ({ name: f.name, type: f.type, value: f.value })),
  //   title
  // });
  const first = useRef();
  const navigate = useNavigate();
  const [formData, setFormData] = React.useState<Partial<T>>(data || {});
  const [errors, setErrors] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    if ((Object.values(data).length > 0 && first.current != true && isEdit==true) || isForm==true) {
      console.log('data123', data)
      setFormData(data);
      first.current = true;
    } else if (formData.zerararea != data.zerararea) {
      let forms = { ...formData };
      forms.area = "";
      forms.role = "";
      forms.role_name = "";
      setFormData(forms);
    } else if (formData.zerarrole != data.zerarrole) {
      let forms = { ...formData };

      forms.role = "";
      forms.role_name = "";
      setFormData(forms);
    }
  }, [data]);

  React.useEffect(() => {
    if (Object.values(formData).length != 0) {
      console.log(formData);
      // formData.role_name=data.role_name;
      setFormDataParent(formData);
    }
  }, [formData]);

  const handleFileChange = (e: any) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.files[0] }));
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    let { name, value, type } = e.target;
    // console.log('🔄 DataForm - handleInputChange:', { name, value, type });
    if (name == "postal" && value?.replace(/-/, "").length == 8) {
      buscar_endereco(value?.replace(/-/, ""));
    }

    if (type === "checkbox") {
      const checkbox = e.target as HTMLInputElement;
      setFormData((prev) => ({ ...prev, [name]: checkbox.checked }));
      if (onChange) {
        onChange(name, checkbox.checked);
      }
    } else if (type === "file") {
      const field = fields.find((f) => f.name === name);
      if (field?.onChange) {
        field.onChange(e);
      }
    } else {
      if (name == "postal") {
        value = maskCep(value);
      }
      if (name == "whatsapp") {
        value = maskPhone(value);
      }
      if (name == "cpf_cnpj") {
        value = maskCpfCnpj(value);
      }
      if (name == "cpf") {
        value = maskCPF(value);
      }
      if (name == "state") {
        value = value
          .toUpperCase()
          .replace(/[^A-Za-z]/g, "")
          .substring(0, 2);
      }

      setFormData((prev) => ({ ...prev, [name]: value }));
      if (onChange) {
        onChange(name, value);
      }
    }
  };

  const handleSelectChange = (e: SelectChangeEvent<string | string[]>) => {
    const { name, value } = e.target;
    // console.log('🔄 DataForm - handleSelectChange:', { name, value });

    setFormData((prev) => ({ ...prev, [name]: value }));

    // Primeiro chamar o onChange específico do campo
    const field = fields.find((f) => f.name === name);
    if (field?.onChange) {
      // console.log('🔄 DataForm - Calling field specific onChange');
      field.onChange(value);
      return;
    }

    // Só propagar para o onChange geral se não houver onChange específico
    if (onChange) {
      onChange(name, value);
    }
  };

  const buscar_endereco = (cep: string) => {
    // this.setState({
    //     loading_cep: true, msgErroCep: '', precos_prazos: [],
    //     shipping: {},
    //     volumes: [],
    // });

    console.log(cep);
    fetch(`https://viacep.com.br/ws/${cep}/json/`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then(async (responseLog) => {
        try {
          let resp = await responseLog.json();
          console.log(resp);
          if (resp.erro == "true") {
            // this.setState({
            //     msgErroCep: 'CEP incorreto',
            //     loading_cep: false
            // }, () => {
            // });
          } else {
            setFormData((prev) => ({
              ...prev,
              ["street"]: resp.logradouro,
              ["state"]: resp.uf,
              ["neighborhood"]: resp.bairro,
              ["city"]: resp.localidade,
            }));

            // setFormData(prev => ({ ...prev, [name]: checkbox.checked }));
          }
        } catch (err) {
          console.log(err);
          // this.setState({ loading_cep: false, msgErroCep: 'Algo deu errado. Seu CEP está correto?' });
        }
      })
      .catch((err) => {
        console.log(err);
        // this.setState({ loading_cep: false, msgErroCep: `Erro ao pegar resposta do servidor. Você está conectado a internet?` });
      });
  };

  const renderField = (field: Field) => {
    // console.log('🎨 DataForm - Rendering field:', {
    //   name: field.name,
    //   type: field.type,
    //   value: field.value
    // });

    const value =
      field.value !== undefined ? field.value : formData[field.name as keyof T];
    const validationError = error?.validationErrors?.[field.name];
    // console.log(error)
    let fieldError: any = field.validationError || validationError?.message;

    switch (field.type) {
      case "section":
        return (
          <p style={{ fontSize: 18 }}>
            <b>{field.label}</b>
          </p>
        );

      case "image":
        return (
          <div>
            <div style={{ position: "relative", display: "flex" }}>
              <input
                type="file"
                accept="image/*"
                id={field.name}
                name={field.name}
                value={field.value}
                onChange={handleFileChange}
                hidden
              />
              <div
                onClick={() => document.getElementById(field.name)?.click()}
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  cursor: "pointer",
                  minWidth: 100,
                  minHeight: 100,
                  width: 100,
                  height: 100,
                  borderRadius: 100,
                  border: "2px solid rgb(37 99 235 / var(--tw-bg-opacity, 1))",
                  padding: 5,
                  overflow: "hidden",
                }}
              >
                <img
                  style={{
                    borderRadius: 100,
                    minWidth: 90,
                    minHeight: 90,
                    width: 90,
                    height: 90,
                    objectFit: "cover",
                  }}
                  src={
                    formData.image
                      ? formData.image instanceof File
                        ? URL.createObjectURL(formData.image)
                        : formData.image
                      : "https://microbiology.ucr.edu/sites/default/files/styles/form_preview/public/blank-profile-pic.png?itok=4teBBoet"
                  }
                />
              </div>
              <div
                onClick={() => document.getElementById(field.name)?.click()}
                style={{
                  cursor: "pointer",
                  boxShadow: "rgba(0, 0, 0, 0.2) -1px 2px 1px 0px",
                  padding: 2,
                  position: "absolute",
                  top: 80,
                  left: 70,
                  background: "white",
                  borderRadius: 100,
                  overflow: "hidden",
                }}
              >
                <Camera width={18} height={18} />
              </div>
              <div style={{ padding: 10, paddingTop: 20 }}>
                <p style={{ fontSize: 16 }}>
                  <b>{formData?.name}</b>
                </p>
                <p style={{ fontSize: 14 }}>{data?.role_name}</p>
              </div>
            </div>
          </div>
        );
      case "document":
        return (
          <div>
            <label className="text-sm font-semibold text-gray-700">
              {field.label}{" "}
              {field.required && <b style={{ color: "red" }}>*</b>}
            </label>
            <div
              style={{ position: "relative", display: "flex" }}
              className="w-full"
            >
              <input
                type="file"
                accept={field.accept || ".docx"}
                id={field.name}
                name={field.name}
                value={field.value}
                onChange={handleFileChange}
                hidden
              />
              {/* <div className='w-full' onClick={()=>document.getElementById(field.name)?.click()} style={{ display:'flex',justifyContent:'center',alignItems:'center', cursor:'pointer',minWidth:100,minHeight:70,  borderRadius: 10, border: '1px solid lightgrey',padding:5, overflow: 'hidden' }}>
                
              </div>
              <div onClick={()=>document.getElementById(field.name)?.click()} style={{ cursor:'pointer',boxShadow: "rgba(0, 0, 0, 0.2) -1px 2px 1px 0px",padding:2,position:'absolute',top:80,left:70,background:'white',borderRadius:100,overflow:'hidden'}}>
                </div> */}
            </div>
            <div
              style={{ cursor: "pointer", height: 118 }}
              onClick={(e) => {
                document.getElementById(field.name)?.click();
              }}
              className="ml-2 flex-1 flex flex-col items-center justify-center gap-2.5 px-[19px] py-5 bg-clarofrente-01 rounded-xl border-[0.5px] border-[#d8e0f0]"
            >
              {/* <CloudIcon className="h-6 w-6 text-clarotextos-02" /> */}
              {formData.document ? (
                <div style={{ cursor: "pointer", color: "grey", fontSize: 14 }}>
                  <FileArchive style={{ width: "100%" }} />
                  <p className="mt-2 flex">
                    <span>Arquivo Selecionado </span>{" "}
                    <Check size={20} style={{ marginLeft: 10 }} color="green" />{" "}
                  </p>
                </div>
              ) : (
                <img src="/cloudupload.png" width={50} />
              )}
            </div>
          </div>
        );
      case "separator":
        return (
          <>
            <hr />
          </>
        );
      case "textarea":
        return (
          <>
            <label className="text-sm font-semibold text-gray-700">
              {field.label}{" "}
              {field.required && <b style={{ color: "red" }}>*</b>}
            </label>
            <Textarea
              required={field.required}
              className={`h-12 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 ${
                fieldError ? "border-red-500" : ""
              }`}
              name={field.name}
              value={String(value || "")}
              onChange={handleInputChange}
              placeholder={field.label}
              disabled={field.disabled}
            />
            {fieldError && (
              <FormHelperText style={{ color: "red" }}>
                {fieldError}
              </FormHelperText>
            )}
            {/* <TextField
            fullWidth
            multiline
            rows={field.rows || 4}
            name={field.name}
            label={field.label}
            value={String(value || '')}
            onChange={handleInputChange}
            required={field.required}
            error={!!fieldError}
            helperText={fieldError}
            placeholder={field.placeholder}
            disabled={field.disabled}
          /> */}
          </>
        );

      case "toogle":
        console.log(value);
        return (
          <div
            style={{
              height: "100%",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <span>{field.label}</span>

              <label className="switch" style={{ marginLeft: 5 }}>
                <input
                  type="checkbox"
                  name={field.name}
                  onChange={handleInputChange}
                  checked={value}
                />
                <span className="slider round"></span>
              </label>
            </div>
          </div>
        );

      case "select":
        // console.log('🎨 DataForm - Rendering select field:', {
        //   name: field.name,
        //   options: field.options,
        //   value
        // });
        return (
          <div>
            <label className="text-sm font-semibold text-gray-700">
              {field.label}{" "}
              {field.required && <b style={{ color: "red" }}>*</b>}
            </label>
            <select
              name={field.name}
              value={value || ""}
              onChange={(e) => handleSelectChange(e)}
              required={field.required}
              disabled={field.disabled}
              label={field.label}
              style={{ fontSize: "0.875rem", padding: 5 }}
              className={`w-full h-12 border rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 ${
                fieldError ? "border-red-500" : ""
              }`}
            >
              <option value="">Selecione uma opção</option>
              {field.options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {/* <Select
              labelId={`${field.name}-label`}
              name={field.name}
              className={`h-12 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 ${fieldError ? "border-red-500" : ""
                }`}
              value={value || ''}
              onChange={handleSelectChange}
              required={field.required}
              disabled={field.disabled}
              label={field.label}
            >
              {field.options?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select> */}
            {fieldError && (
              <FormHelperText style={{ color: "red" }}>
                {fieldError}
              </FormHelperText>
            )}
          </div>
        );

      case "multiselect":
        return (
          <FormControl fullWidth error={!!fieldError} size="small">
            <InputLabel id={`${field.name}-label`}>{field.label}</InputLabel>
            <Select
              labelId={`${field.name}-label`}
              name={field.name}
              multiple
              className={`h-12 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 ${
                fieldError ? "border-red-500" : ""
              }`}
              value={value || []}
              onChange={handleSelectChange}
              required={field.required}
              disabled={field.disabled}
              label={field.label}
              displayEmpty
            >
              {field.options?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {fieldError && <FormHelperText>{fieldError}</FormHelperText>}
          </FormControl>
        );

      case "checkbox":
        return (
          <FormControlLabel
            control={
              <Checkbox
                name={field.name}
                checked={Boolean(value)}
                onChange={handleInputChange}
                disabled={field.disabled}
              />
            }
            label={field.label}
          />
        );

      case "radio":
        return (
          <FormControl component="fieldset" error={!!fieldError}>
            <FormLabel component="legend">{field.label}</FormLabel>
            <RadioGroup
              name={field.name}
              value={String(value || "")}
              onChange={handleInputChange}
            >
              {field.options?.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio />}
                  label={option.label}
                  disabled={field.disabled}
                />
              ))}
            </RadioGroup>
            {fieldError && <FormHelperText>{fieldError}</FormHelperText>}
          </FormControl>
        );

      case "date":
      case "datetime-local":
        return (
          <FormControl fullWidth error={!!fieldError}>
            <FormLabel
              sx={{
                mb: 1,
                fontSize: "0.875rem",
                "&.Mui-focused": { color: "primary.main" },
              }}
            >
              {field.label}
              {field.required && " *"}
            </FormLabel>
            <TextField
              fullWidth
              type={field.type}
              name={field.name}
              value={String(value || "")}
              onChange={handleInputChange}
              required={field.required}
              error={!!fieldError}
              helperText={fieldError}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{
                "& .MuiInputBase-input": {
                  padding: "8px 12px",
                },
              }}
            />
          </FormControl>
        );

      case "select-builder":
        return (
          <Box>
            <Typography variant="body1">{field.label}</Typography>
            <SelectOptions
              options={field.selectOptions || []}
              onChange={field.onSelectOptionsChange || (() => {})}
            />
          </Box>
        );

      case "file":
        return (
          <FormControl fullWidth error={field.error}>
            <FormLabel
              sx={{
                mb: 1,
                fontSize: "0.875rem",
                "&.Mui-focused": { color: "primary.main" },
              }}
            >
              {field.label}
              {field.required && " *"}
            </FormLabel>
            <TextField
              fullWidth
              type="file"
              name={field.name}
              onChange={handleInputChange}
              required={field.required}
              error={field.error}
              helperText={field.validationError || field.helperText}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                accept: field.accept,
              }}
              size="small"
            />
          </FormControl>
        );

      case "logo":
        // console.log('renderField logo:', {
        //   fieldName: field.name,
        //   value,
        //   formData: formData[field.name as keyof T],
        //   previewUrl: field.previewUrl
        // });
        return (
          <LogoField
            name={field.name}
            label={field.label}
            accept={field.accept}
            helperText={field.helperText}
            error={field.error}
            validationError={field.validationError}
            previewUrl={field.previewUrl}
            value={value}
            onChange={handleInputChange}
            required={field.required}
          />
        );

      default:
        return (
          <div>
            {/* <TextField
              fullWidth
              type={field.type || 'text'}
              name={field.name}
              label={field.label}
              value={String(value || '')}
              onChange={handleInputChange}
              required={field.required}
              error={!!fieldError}
              helperText={fieldError}
              InputProps={{
                inputProps: {
                  min: field.min,
                  max: field.max,
                  pattern: field.pattern
                }
              }}
              placeholder={field.placeholder}
              disabled={field.disabled}
              size="small"
            /> */}
            <label className="text-sm font-semibold text-gray-700">
              {field.label}{" "}
              {field.required && <b style={{ color: "red" }}>*</b>}
            </label>
            <Input
              required={field.required}
              className={`h-12 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 ${
                fieldError ? "border-red-500" : ""
              }`}
              type={field.type || "text"}
              name={field.name}
              value={String(value || "")}
              onChange={handleInputChange}
              placeholder={field.label}
              disabled={field.disabled}
            />
            {fieldError && (
              <FormHelperText style={{ color: "red" }}>
                {fieldError}
              </FormHelperText>
            )}
          </div>
        );
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error("Erro ao salvar:", error);
      setErrors({ submit: "Erro ao salvar os dados" });
    }
  };

  return (
    <div
      className={
        "flex flex-col flex-1 shrink  self-stretch basis-0 min-w-60 max-md:px-5 bg-background-dark " +
        (onClose ? "" : "px-6 py-7 pb-2")
      }
    >
      {onClose == undefined && (
        <h1 className="text-2xl font-bold mb-6">
          {isEdit ? `${title}` : `${title}`}
        </h1>
      )}

      {/* <div className="mb-6 p-4  rounded-lg"> */}
      <Card
        className={
          onClose
            ? '"w-full custom-scrollbar border-none bg-white'
            : "w-full custom-scrollbar border border-[#D8E0F0] rounded-[14px] bg-[#FCFCFC] hover:border-[#c8d0e0] transition-colors"
        }
      >
        <CardContent
          onClose={onClose}
          className={onClose ? "" : "p-[20px] "}
          style={{ position: "relative" }}
        >
          <div
            style={{
              maxHeight: window.innerHeight - 150,
              overflow: "scroll",
              paddingBottom: 50,
            }}
            className="custom-scrollbar"
          >
            {/* {error && (
        <ErrorDisplay
          error={error}
          onClose={onClearError}
          showDetails={true}
        />
      )} */}
            <Box
              component="form"
              onSubmit={handleSubmit}
              style={{ paddingBottom: 15 }}
            >
              <Stack spacing={3}>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                    position: "relative",
                  }}
                >
                  {fields.map((field) => (
                    <div
                      className="mt-4"
                      style={{
                        width: field.col ? field.col : "100%",
                        minWidth: 200,
                      }}
                      key={field.name}
                    >
                      {renderField(field)}
                    </div>
                  ))}
                  {children && (
                    <div
                      className="mt-4"
                      style={{ width: "100%", minWidth: 200 }}
                    >
                      {children()}
                    </div>
                  )}
                </div>
              </Stack>
              <button hidden type="submit" id={title + id_submit}></button>
            </Box>
          </div>
          {!hideButtons && (
            <Box
              style={{
                display: "flex",
                gap: 2,
                justifyContent: "flex-end",
                position: "absolute",
                bottom: 0,
                right: 0,
                left: 0,
                padding: onClose == undefined ? 20 : 0,
                backgroundColor: "white",
                borderRadius: 20,
              }}
            >
              {!onClose && (
                <Button
                  type="button"
                  onClick={() =>
                    onCancel ? onCancel() : navigate(`/${basePath}`)
                  }
                  variant="outlined"
                  disabled={disabled}
                >
                  Cancelar
                </Button>
              )}
              <LoadingButton
                type="button"
                onClick={(e) => {
                  // handleSubmit(e);
                  document.getElementById(title + id_submit).click();
                }}
                variant="contained"
                color="primary"
                loading={disabled}
                disabled={disabled}
              >
                Salvar
              </LoadingButton>
            </Box>
          )}
        </CardContent>
      </Card>
      {/* </div> */}
    </div>
  );
};

// Componente para gerenciar opções do SELECT
const SelectOptions: React.FC<{
  options: SelectOption[];
  onChange: (options: SelectOption[]) => void;
}> = ({ options = [], onChange }) => {
  const handleOptionChange = (index: number, key: string, value: string) => {
    const newOptions = [...options];
    newOptions[index] = { ...newOptions[index], [key]: value };
    onChange(newOptions);
  };

  const handleAddOption = () => {
    onChange([...options, { key: "", value: "" }]);
  };

  const handleRemoveOption = (index: number) => {
    onChange(options.filter((_, i) => i !== index));
  };

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      {options.map((option, index) => (
        <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
          <Grid item xs={5}>
            <TextField
              fullWidth
              size="small"
              label="Texto"
              value={option.key}
              onChange={(e) => handleOptionChange(index, "key", e.target.value)}
            />
          </Grid>
          <Grid sx={{ display: option.hidden ? "none" : "block" }} item xs={5}>
            <TextField
              fullWidth
              size="small"
              label="Valor"
              value={option.value}
              onChange={(e) =>
                handleOptionChange(index, "value", e.target.value)
              }
            />
          </Grid>
          <Grid item xs={2} sx={{ display: "flex", alignItems: "center" }}>
            <IconButton
              size="small"
              color="error"
              onClick={() => handleRemoveOption(index)}
            >
              <DeleteIcon />
            </IconButton>
          </Grid>
        </Grid>
      ))}
      <Button
        startIcon={<AddIcon />}
        onClick={handleAddOption}
        variant="outlined"
        size="small"
        sx={{ mt: 1 }}
      >
        Adicionar Opção
      </Button>
    </Box>
  );
};

export default DataForm;
